import styles from "./post-capture-button-container.less";
import { useMemo } from "react";
import { CaptureStep } from "@idw-types/data/capture-step";
import { updateCaptureStep } from "@redux/actions/verification-step-config-actions";
import { useDispatch, useSelector } from "react-redux";
import { getBase64Size, getFormattedFileSize } from "@utils/image-util";
import { Logger } from "@utils/logger";
import { getCaptureStepValidationResults } from "@redux/selectors/validation-results-selectors";
import { PrimaryButton } from "../primary-button/primary-button";
import { SecondaryButton } from "../secondary-button/secondary-button";
import { getLanguageContent } from "@redux/selectors/language-selectors";
import { AuditLogTypes } from "@constants/audit-log-types";
import { useAuditLogger } from "@hooks/use-audit-logger";

type PostCaptureButtonContainerProps = {
    captureStep: CaptureStep;
    imageUrl: string;
    resetCaptureStepPage: () => void;
};

export const PostCaptureButtonContainer = (props: PostCaptureButtonContainerProps) => {
    const { captureStep, imageUrl, resetCaptureStepPage } = props;
    const dispatch = useDispatch();
    const { writeAuditLog } = useAuditLogger();
    //@ts-ignore
    const captureStepValidationResults = useSelector(state => getCaptureStepValidationResults(state, captureStep.type));
    const content = useSelector(getLanguageContent);

    const onClickSaveAndNext = () => {
        try {
            const captureSize = getBase64Size(imageUrl);
            const formattedCaptureSize = getFormattedFileSize(captureSize);
            const [head] = imageUrl.split(',');
            Logger.info(`[Image Metadata] Completed capture of (${captureStep.type}) with size: ${formattedCaptureSize}, raw: ${captureSize}, file type: ${head} `);
            if (captureSize < 100000) {
                Logger.warn(`[Image Metadata] capture is less than 100KB`);
            }
            if (captureSize < 50000) {
                Logger.warn(`[Image Metadata] capture is less than 50KB`);
            }
        } catch (e) {
            Logger.warn(`[Image Metadata] capture metadata error`, e);
        }

        writeAuditLog(AuditLogTypes.DCUI_SAVE_BUTTON_HIT);
        dispatch(updateCaptureStep({ ...captureStep, completed: true, data: imageUrl }));
    };

    const onClickRetry = () => {
        writeAuditLog(AuditLogTypes.DCUI_DISCARD_BUTTON_HIT);
        resetCaptureStepPage();
    };

    const showSaveAndNext = useMemo(() => {
        return Object.values(captureStepValidationResults).every(result => result.isValid || result.isDisabled || !result.isRequired);
    }, [captureStepValidationResults]);

    const saveAndNextButtonText = content.general.continueButtonText;
    const discardButtonText = content.general.discardButtonText;

    return (
        <div className={styles.postCaptureButtonContainer}>
            {showSaveAndNext && (
                <PrimaryButton
                    hideSpinner={true}
                    disabled={false}
                    onClick={onClickSaveAndNext}
                    className={styles.postCaptureButton}
                    type={"button"}
                    id={"save-capture"}
                    label={saveAndNextButtonText}
                />
            )}
            {showSaveAndNext && (
                <SecondaryButton
                    hideSpinner={true}
                    disabled={false}
                    onClick={onClickRetry}
                    className={styles.postCaptureButton}
                    type={"button"}
                    id={"async-continue"}
                    label={discardButtonText}
                />
            )}
            {!showSaveAndNext && (
                <PrimaryButton
                    hideSpinner={true}
                    disabled={false}
                    onClick={onClickRetry}
                    className={styles.postCaptureButton}
                    type={"button"}
                    id={"async-continue"}
                    label={discardButtonText}
                />
            )}
        </div>
    );
};